import L from "leaflet";

type IndoorFeature = GeoJSON.Feature<GeoJSON.Geometry, Record<string, unknown>>;

type IndoorOptions = L.GeoJSONOptions & {
    level?: string;
    getLevel?: (feature: IndoorFeature) => string | number | Array<string | number> | undefined;
    markerForFeature?: (feature: IndoorFeature) => L.Layer | undefined;
};

type IndoorLayerInstance = {
    _map: L.Map | undefined;
    _level: string | undefined;
    _levels: Record<string, L.FeatureGroup>;
    options: IndoorOptions;
    addData: (data: GeoJSON.GeoJsonObject | IndoorFeature[]) => void;
    getLevels: () => string[];
    getLevel: () => string | undefined;
    setLevel: (level: string | { newLevel: string }) => void;
};

/**
 * Enregistre l'API historique de leaflet-indoor sur l'instance Leaflet utilisée
 * par l'application.
 *
 * Le paquet npm embarque une ancienne instance de Leaflet. Lorsqu'il est chargé
 * avec Leaflet 1.9, L.Indoor peut donc être enregistré sur une autre instance et
 * rester indisponible pour la carte React. Cet adaptateur reprend l'API publique
 * du plugin (Indoor, getLevels, setLevel et Control.Level) avec l'instance L
 * réellement importée par l'application.
 */
export const registerLeafletIndoor = (leaflet: typeof L = L): void => {
    const leafletWithIndoor = leaflet as typeof L & {
        Indoor?: new (data: GeoJSON.GeoJsonObject, options?: IndoorOptions) => IndoorLayerInstance;
        Control: typeof L.Control & { Level?: new (options: LevelControlOptions) => L.Control };
    };

    if (leafletWithIndoor.Indoor && leafletWithIndoor.Control.Level) {
        return;
    }

    const Indoor = (leaflet.Layer as any).extend({
        initialize(this: IndoorLayerInstance, data: GeoJSON.GeoJsonObject, options: IndoorOptions = {}) {
            leaflet.setOptions(this, options);
            this.options = options;
            this._map = undefined;
            this._levels = {};
            this._level = options.level;
            this.addData(data);
        },

        addData(this: IndoorLayerInstance, data: GeoJSON.GeoJsonObject | IndoorFeature[]) {
            const features = Array.isArray(data)
                ? data
                : (data as GeoJSON.FeatureCollection).features ?? [];

            features.forEach((feature) => {
                const getLevel = this.options.getLevel ?? ((item: IndoorFeature) => item.properties?.level as string | undefined);
                const rawLevel = getLevel(feature as IndoorFeature);
                const levels = Array.isArray(rawLevel) ? rawLevel : [rawLevel];

                levels
                    .filter((level): level is string | number => level !== undefined && level !== null)
                    .forEach((levelValue) => {
                        const level = String(levelValue);
                        const group = this._levels[level] ?? (this._levels[level] = leaflet.featureGroup());
                        const userOnEachFeature = this.options.onEachFeature;

                        const geoJsonLayer = leaflet.geoJSON(feature as GeoJSON.GeoJsonObject, {
                            ...this.options,
                            onEachFeature: (currentFeature, layer) => {
                                userOnEachFeature?.(currentFeature, layer);
                            }
                        });

                        group.addLayer(geoJsonLayer);

                        const marker = this.options.markerForFeature?.(feature as IndoorFeature);
                        if (marker) {
                            group.addLayer(marker);
                        }
                    });
            });
        },

        onAdd(this: IndoorLayerInstance, map: L.Map) {
            this._map = map;
            const levels = this.getLevels();
            if (!this._level) {
                this._level = levels[0];
            }
            if (this._level && this._levels[this._level]) {
                map.addLayer(this._levels[this._level]);
            }
        },

        onRemove(this: IndoorLayerInstance, map: L.Map) {
            Object.values(this._levels).forEach((layer) => {
                if (map.hasLayer(layer)) {
                    map.removeLayer(layer);
                }
            });
            this._map = undefined;
        },

        getLevels(this: IndoorLayerInstance): string[] {
            return Object.keys(this._levels);
        },

        getLevel(this: IndoorLayerInstance): string | undefined {
            return this._level;
        },

        setLevel(this: IndoorLayerInstance, input: string | { newLevel: string }) {
            const level = typeof input === "object" ? input.newLevel : String(input);
            if (level === this._level) {
                return;
            }

            if (this._map && this._level && this._levels[this._level] && this._map.hasLayer(this._levels[this._level])) {
                this._map.removeLayer(this._levels[this._level]);
            }

            this._level = level;

            if (this._map && this._levels[level]) {
                this._map.addLayer(this._levels[level]);
            }
        }
    });

    const LevelControl = (leaflet.Control as any).extend({
        includes: (leaflet.Evented as any).prototype,

        initialize(this: any, options: LevelControlOptions) {
            leaflet.setOptions(this, options);
            this._level = options.level;
        },

        onAdd(this: any) {
            const container = leaflet.DomUtil.create("div", "leaflet-bar leaflet-control");
            const levels = [...this.options.levels].sort((a: string, b: string) => Number(b) - Number(a));

            levels.forEach((level: string) => {
                const button = leaflet.DomUtil.create("a", "leaflet-button-part", container);
                button.href = "#";
                button.textContent = level;
                button.title = `Niveau ${level}`;
                button.style.backgroundColor = level === this._level ? "#d8e6f3" : "#ffffff";

                leaflet.DomEvent.on(button, "click", leaflet.DomEvent.stop)
                    .on(button, "click", () => {
                        const oldLevel = this._level;
                        this._level = level;
                        Array.from(container.querySelectorAll("a")).forEach((element) => {
                            (element as HTMLElement).style.backgroundColor = "#ffffff";
                        });
                        button.style.backgroundColor = "#d8e6f3";
                        this.fire("levelchange", { oldLevel, newLevel: level });
                    });
            });

            leaflet.DomEvent.disableClickPropagation(container);
            return container;
        },

        addEventListener(this: any, type: string, listener: (...args: any[]) => void, context?: unknown) {
            return this.on(type, listener, context);
        }
    });

    leafletWithIndoor.Indoor = Indoor;
    leafletWithIndoor.Control.Level = LevelControl;
};

interface LevelControlOptions extends L.ControlOptions {
    level: string;
    levels: string[];
}
